import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * `navigator.onLine`, as a subscription. It is the only signal a phone has *outside* a session
 * — `/guest`, `/tv` and the auth screens have no RTDB listener — and it is the one that flips
 * first when the network goes away.
 *
 * Shared by the banner and by the screens that have to heal when it comes back: QA 2026-09-15
 * **UX-480** found the banner clearing itself the moment the link returned while the Buscar
 * panel two centimetres below still read "No hay conexión. Revisa tu red." with 0 results, for
 * as long as it was left alone.
 */
export function useOnline(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener('online', onChange);
      window.addEventListener('offline', onChange);
      return () => {
        window.removeEventListener('online', onChange);
        window.removeEventListener('offline', onChange);
      };
    },
    () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    () => true,
  );
}

/**
 * Runs `onOnline` when the browser announces the link is back — the **transition**, not the
 * state, which is what a screen that failed while offline has to react to (UX-480). Reacting to
 * the state instead would re-run the query on every error that happens *while* online, which is
 * a loop; the event fires once, when there is something new to try.
 */
export function useOnlineRecovery(enabled: boolean, onOnline: () => void): void {
  const handler = useRef(onOnline);
  useEffect(() => {
    handler.current = onOnline;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const heal = () => handler.current();
    window.addEventListener('online', heal);
    return () => window.removeEventListener('online', heal);
  }, [enabled]);
}
